import { useCallback, useMemo } from "react";

import type { DayOfTheWeek, InputMaybe } from "@/lib/gql/__generated__/graphql";

import type { ForArrayType } from "@/components/features/_model/types/auto-generator-for-view-model";
import { DAY_OF_THE_WEEK_LIST } from "@/components/features/items/edit/_shared/constants/day-of-the-week";

import type { FormOptionType } from "@/components/ui/form/_types/type";

import type { CheckedState } from "@radix-ui/react-checkbox";

type Props = {
	stayableDayOfTheWeek:
		| ForArrayType<InputMaybe<DayOfTheWeek[]> | undefined>
		| ForArrayType<DayOfTheWeek[]>
		| undefined;
	onSetViewModel: (
		values: {
			key: "stayableDayOfTheWeek";
			value: ForArrayType<DayOfTheWeek[]>;
		}[],
	) => void;
};

export const useStayableDayOfTheWeek = ({ stayableDayOfTheWeek, onSetViewModel }: Props) => {
	const rawStayableDayOfTheWeek = useMemo(
		() => stayableDayOfTheWeek?.value?.map((dayOfTheWeek) => dayOfTheWeek.value) ?? [],
		[stayableDayOfTheWeek],
	);

	const stayableDayOfTheWeekOptions = useMemo(
		() =>
			DAY_OF_THE_WEEK_LIST.map((dayOfTheWeek, idx) => {
				return {
					...dayOfTheWeek,
					id: `stayableDayOfTheWeekOptions_${dayOfTheWeek.value}_${idx}`,
				} satisfies FormOptionType;
			}),
		[],
	);

	const onSetStayableDayOfTheWeek = useCallback(
		(_input: CheckedState, item: FormOptionType) => {
			const dayOfTheWeek = item.value as DayOfTheWeek;

			const newRawStayableDayOfTheWeek = rawStayableDayOfTheWeek?.includes(dayOfTheWeek)
				? rawStayableDayOfTheWeek.filter((item) => item !== dayOfTheWeek)
				: rawStayableDayOfTheWeek?.length
					? [...rawStayableDayOfTheWeek, dayOfTheWeek]
					: [dayOfTheWeek];

			const newStayableDayOfTheWeek = {
				isEdited: true,
				hasError: false,
				value: newRawStayableDayOfTheWeek?.map((dayOfTheWeek) => {
					return {
						isEdited: true,
						value: dayOfTheWeek,
					};
				}),
			};

			onSetViewModel([
				{
					key: "stayableDayOfTheWeek",
					value: newStayableDayOfTheWeek,
				},
			]);
		},
		[onSetViewModel, rawStayableDayOfTheWeek],
	);

	return {
		stayableDayOfTheWeek,
		rawStayableDayOfTheWeek,
		stayableDayOfTheWeekOptions,
		onSetStayableDayOfTheWeek,
	};
};
